
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetraaMySQLAgent () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span>Database</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">MySQL</span>
        </nav>
    
            {/* Intro */}
            <div className="mb-10">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Netraa MySQL Monitoring Agent
                </span>
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This guide explains how to install and configure the MySQL Monitoring Agent on your server using Netraa's offline installer.
              </p>
            </div>
    
            {/* Deployment Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { title: "Deployment Environment", value: "Offline / Air-Gapped" },
                { title: "Operating System", value: "RHEL / Ubuntu" },
                { title: "Monitoring Tool", value: "Netraa MySQL Agent" },
              ].map((item, idx) => (
                <div key={idx} className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl border border-border-color dark:border-border-dark shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-text-muted mb-1">{item.title}</p>
                  <p className={`text-sm font-bold ${idx === 0 ? "text-primary" : "text-text-main dark:text-white"}`}>{item.value}</p>
                </div>
              ))}
            </div>
    
            {/* Important Note */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 mb-12 rounded-r-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-sm">warning</span>
                <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">IMPORTANT NOTE</h4>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                Ensure your MySQL server is running and accessible. You will need root credentials to configure the monitoring agent.
              </p>
            </div>

             {/* Prerequisites */}
            <section className="mb-10 scroll-mt-24" id="prerequisites">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">fact_check</span>
                </span>
                Prerequisites
              </h2>
    
              <div className="prose dark:prose-invert max-w-none text-sm space-y-2 mb-8">
                <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
                  <li>A database user with the following access:</li>
                </ul>
                <CodeBlock code={`
CREATE USER '<username>'@'<host_ip>' IDENTIFIED BY '<password>' WITH MAX_USER_CONNECTIONS 3;


    GRANT PROCESS, REPLICATION CLIENT, SELECT ON *.* TO '<username>'@'<host_ip>';


    FLUSH PRIVILEGES;"`} />
              </div>
            </section>
    
            {/* Installation Section */}
            <section className="mb-16 scroll-mt-24" id="installation">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-primary border border-orange-200 dark:border-orange-900/50">
                  <span className="material-symbols-outlined text-[24px]">download</span>
                </span>
                Installation Steps
              </h2>
    
              <div className="prose dark:prose-invert max-w-none text-sm space-y-4 mb-8">
                <p>Follow these steps to install the MySQL Monitoring Agent:</p>
                <ol className="list-decimal pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Download the agent from the provided ApMoSys share point and unzip it:
                    <CodeBlock code={`unzip mysql_agent.zip -d mysql_agent\ncd mysql_agent`} />
                  </li>
                  <li>Run the installer (requires sudo):
                    <CodeBlock code={`sudo ./install.sh`} />
                  </li>
                  <li>Follow the on-screen menu:
                    <CodeBlock code={`==============================================\n    APMOSYS MySQL Monitoring\n==============================================\n1) Install monitoring services\n2) Uninstall monitoring services\n3) Exit`} />
                  </li>
                  <li>If you choose <strong>Install</strong>, the script will prompt for:
                    <ul className="list-disc pl-5 space-y-1">
                      <li>MySQL root username and password</li>
                      <li>MySQL server IP (press Enter for default)</li>
                      <li>MySQL server port (default = 3306)</li>
                    </ul>
                  </li>
                </ol>
              </div>
    
              <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 p-6 rounded-xl flex items-center gap-6">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white shrink-0">
                  <span className="material-symbols-outlined text-[28px]">check_circle</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-green-800 dark:text-green-300">Installation Complete!</h4>
                  <p className="text-sm text-green-700 dark:text-green-400">The MySQL Monitoring Agent is now installed and ready to use.</p>
                </div>
              </div>
            </section>
    
            {/* Usage Section */}
            <section className="mb-16 scroll-mt-24" id="usage">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">play_circle</span>
                </span>
                Usage
              </h2>
    
              <div className="prose dark:prose-invert max-w-none text-sm space-y-2 mb-8">
                <p>After installation, the agent automatically shows the status of each monitoring service. No manual checks are required.</p>
                <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
                  <li>Check service status anytime using the installer menu.</li>
                  <li>Uninstall monitoring services using the menu option if needed.</li>
                </ul>
              </div>
            </section>
    
           {/* Environment Table */}
    <section className="mb-16 scroll-mt-24" id="environment">
    
      <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
    
        <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 border border-green-200 dark:border-green-900/50">
    
          <span className="material-symbols-outlined text-[24px]">
            grid_view
          </span>
    
        </span>
    
        Environment Specifications
      </h2>
    
      {/* OUTER WRAPPER (clips scrollbar inside rounded border) */}
      <div className="border border-border-color dark:border-border-dark rounded-xl overflow-hidden">
    
        {/* SCROLL CONTAINER */}
        <div className="overflow-x-auto w-full">
    
          <table className="w-full min-w-[520px] text-left border-collapse">
    
            {/* HEADER */}
            <thead className="text-[11px] bg-gray-50 dark:bg-white/5">
    
              <tr>
    
                <th className="px-4 py-4 font-bold uppercase text-text-muted whitespace-nowrap">
                  Parameter
                </th>
    
                <th className="px-4 py-4 font-bold uppercase text-text-muted whitespace-nowrap">
                  Value / Description
                </th>
    
              </tr>
            </thead>
    
            {/* BODY */}
            <tbody className="text-[12px] divide-y divide-border-color dark:divide-border-dark">
    
              <tr className="bg-white dark:bg-transparent">
    
                <td className="px-4 py-4 font-bold text-primary whitespace-nowrap">
                  MySQL Root Username
                </td>
    
                <td className="px-4 py-4 text-text-main dark:text-white">
                  Required for monitoring configuration
                </td>
    
              </tr>
    
              <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
    
                <td className="px-4 py-4 text-text-main dark:text-white whitespace-nowrap">
                  MySQL Root Password
                </td>
    
                <td className="px-4 py-4 text-text-main dark:text-white">
                  Required for monitoring configuration
                </td>
    
              </tr>
    
              <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
    
                <td className="px-4 py-4 text-text-main dark:text-white whitespace-nowrap">
                  Server IP
                </td>
    
                <td className="px-4 py-4 text-text-main dark:text-white">
                  Default = current host IP
                </td>
    
              </tr>
    
              <tr className="bg-gray-50/50 dark:bg-white/[0.02]">
    
                <td className="px-4 py-4 text-text-main dark:text-white whitespace-nowrap">
                  Server Port
                </td>
    
                <td className="px-4 py-4 text-text-main dark:text-white">
                  Default = 3306
                </td>
    
              </tr>
    
            </tbody>
          </table>
    
        </div>
      </div>
    </section>
          </div>
        </main>
      </div>
    );
    }